/*
  Disney bags template (templates/product.disney-bags.json).

  - Main section (sections/fm-disney-main.liquid): Impulse's own theme.Variants drives the option inputs and the
    form's variant select; this file repaints the price, the Add to cart state, the instalments line and the
    option labels on each change. Zoom is Impulse's theme.Photoswipe on the gallery. On a drawer cart setup the
    form goes through theme.AjaxProduct, as on the default product page; on a page cart it posts normally.
  - Gallery (snippets/fm-disney-gallery.liquid): on mobile, keeps the thumbnail row in step with the swipe track.
  - Feature slider (sections/fm-disney-features.liquid): prev and next buttons, pager dots and auto slide.
  - Related products (sections/fm-disney-related.liquid): loads the tagged products, then the best sellers
    collection, through the Section Rendering API, closest in price first, as Impulse's product cards in a row the
    prev and next buttons page by whole cards.

  Runs after assets/theme.js (both deferred, theme.js first in the document).
*/
(function () {
  'use strict';

  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function formatMoney(cents) {
    if (window.theme && theme.Currency && theme.settings && theme.settings.moneyFormat) {
      return theme.Currency.formatMoney(cents, theme.settings.moneyFormat);
    }
    return '$' + (cents / 100).toFixed(2);
  }

  /* Price with the cents raised: "$109.00" becomes "$109." and a raised "00". */
  function priceHtml(cents) {
    var text = formatMoney(cents);
    var cut = text.lastIndexOf('.');
    if (cut === -1) return text;
    var span = document.createElement('span');
    span.textContent = text.slice(0, cut + 1);
    var sup = document.createElement('sup');
    sup.className = 'fm-disney__cents';
    sup.textContent = text.slice(cut + 1);
    return span.innerHTML + sup.outerHTML;
  }

  /* ---------- Gallery ---------- */

  function Gallery(el, sectionId) {
    this.el = el;
    this.track = el.querySelector('[data-fm-track]');
    this.tiles = Array.prototype.slice.call(this.track.children);
    this.thumbs = Array.prototype.slice.call(el.querySelectorAll('[data-fm-thumb]'));

    if (window.theme && theme.Photoswipe) {
      new theme.Photoswipe(el, sectionId);
    }

    if (this.thumbs.length) {
      this.thumbs.forEach(function (thumb) {
        thumb.addEventListener('click', function () {
          this.goTo(parseInt(thumb.dataset.fmThumb, 10));
        }.bind(this));
      }, this);

      var ticking = false;
      this.track.addEventListener('scroll', function () {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(function () {
          ticking = false;
          this.setActive(this.currentIndex());
        }.bind(this));
      }.bind(this), { passive: true });
    }
  }

  Gallery.prototype.isCarousel = function () {
    return this.track.scrollWidth > this.track.clientWidth + 1;
  };

  Gallery.prototype.currentIndex = function () {
    var track = this.track;
    var max = track.scrollWidth - track.clientWidth;
    if (max > 0 && track.scrollLeft >= max - 2) return this.tiles.length - 1;
    var origin = this.tiles[0].offsetLeft;
    var best = 0;
    var bestDistance = Infinity;
    this.tiles.forEach(function (tile, index) {
      var distance = Math.abs(tile.offsetLeft - origin - track.scrollLeft);
      if (distance < bestDistance) {
        bestDistance = distance;
        best = index;
      }
    });
    return best;
  };

  Gallery.prototype.setActive = function (index) {
    this.thumbs.forEach(function (thumb, i) {
      var active = i === index;
      thumb.classList.toggle('is-active', active);
      if (active) {
        thumb.setAttribute('aria-current', 'true');
      } else {
        thumb.removeAttribute('aria-current');
      }
    });
  };

  Gallery.prototype.goTo = function (index) {
    var tile = this.tiles[index];
    if (!tile) return;
    if (this.isCarousel()) {
      this.track.scrollTo({
        left: tile.offsetLeft - this.tiles[0].offsetLeft,
        behavior: reduceMotion ? 'auto' : 'smooth'
      });
    }
    this.setActive(index);
  };

  Gallery.prototype.goToMedia = function (mediaId) {
    for (var i = 0; i < this.tiles.length; i++) {
      if (String(this.tiles[i].dataset.mediaId) === String(mediaId)) {
        this.goTo(i);
        return;
      }
    }
  };

  /* ---------- Main section ---------- */

  function Main(container) {
    this.container = container;
    this.sectionId = container.dataset.sectionId;

    var galleryEl = container.querySelector('[data-fm-gallery]');
    this.gallery = galleryEl ? new Gallery(galleryEl, this.sectionId) : null;

    var form = container.querySelector('.product-single__form');
    if (form && window.theme && theme.AjaxProduct && theme.settings && theme.settings.cartType === 'drawer') {
      new theme.AjaxProduct(form);
    }

    var json = container.querySelector('[data-fm-variants]');
    var inputs = container.querySelectorAll('[data-variant-input]');
    if (!json || !inputs.length || !(window.theme && theme.Variants)) return;

    var variants;
    try {
      variants = JSON.parse(json.textContent);
    } catch (error) {
      return;
    }

    this.variants = new theme.Variants({
      container: container,
      enableHistoryState: container.dataset.history === 'true',
      singleOptionSelector: '[data-variant-input]',
      originalSelectorId: '[data-product-select]',
      variants: variants,
      dynamicVariantsEnabled: true
    });

    container.addEventListener('variantChange', this.onVariantChange.bind(this));
    container.addEventListener('variantImageChange', function (evt) {
      var variant = evt.detail && evt.detail.variant;
      if (this.gallery && variant && variant.featured_media) {
        this.gallery.goToMedia(variant.featured_media.id);
      }
    }.bind(this));
  }

  Main.prototype.onVariantChange = function (evt) {
    var detail = evt.detail || {};
    var variant = detail.variant;
    var container = this.container;

    (detail.currentlySelectedValues || []).forEach(function (selected) {
      var label = container.querySelector('[data-fm-option-label="' + selected.index + '"]');
      if (label) label.textContent = selected.value;
    });

    var button = container.querySelector('[data-add-to-cart]');
    var buttonText = container.querySelector('[data-add-to-cart-text]');
    if (button && buttonText) {
      var strings = (window.theme && theme.strings) || {};
      if (variant && variant.available) {
        button.disabled = false;
        button.classList.remove('disabled');
        buttonText.textContent = buttonText.dataset.defaultText;
      } else {
        button.disabled = true;
        button.classList.add('disabled');
        buttonText.textContent = variant ? (strings.soldOut || 'Sold out') : (strings.unavailable || 'Unavailable');
      }
    }

    if (!variant) return;

    var price = container.querySelector('[data-fm-price]');
    var compare = container.querySelector('[data-fm-compare]');
    var save = container.querySelector('[data-fm-save]');
    var onSale = variant.compare_at_price > variant.price;

    if (price) {
      price.innerHTML = priceHtml(variant.price);
      price.classList.toggle('on-sale', onSale);
    }
    if (compare) {
      compare.innerHTML = onSale ? formatMoney(variant.compare_at_price) : '';
      compare.classList.toggle('hide', !onSale);
    }
    if (save) {
      var savings = '';
      if (onSale) {
        var difference = variant.compare_at_price - variant.price;
        savings = (window.theme && theme.settings && theme.settings.saveType === 'percent')
          ? Math.round(difference * 100 / variant.compare_at_price) + '%'
          : formatMoney(difference);
        var template = (window.theme && theme.strings && theme.strings.savePrice) || 'Save [saved_amount]';
        save.innerHTML = template.replace('[saved_amount]', savings);
      } else {
        save.innerHTML = '';
      }
      save.classList.toggle('hide', !onSale);
    }

    var instalment = container.querySelector('[data-fm-instalment]');
    if (instalment) instalment.textContent = formatMoney(Math.floor(variant.price / 4));

    var paypal = container.querySelector('[data-pp-message]');
    if (paypal) paypal.setAttribute('data-pp-amount', (variant.price / 100).toFixed(2));
  };

  /* ---------- Slider helpers (ported from OzMobiles' assets/photo-text-rows.js) ---------- */

  var reduceMotionQuery = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)');

  function prefersReducedMotion() {
    return Boolean(reduceMotionQuery && reduceMotionQuery.matches);
  }

  // Card left edges and the snap line are in track coordinates. Returns the scrollLeft that puts the next (or
  // previous) card on the snap line, or null when there is none.
  function targetScrollLeft(cardLefts, scrollLeft, snapPadding, direction) {
    var snapLine = scrollLeft + snapPadding;
    var i;

    if (direction > 0) {
      for (i = 0; i < cardLefts.length; i++) {
        if (cardLefts[i] > snapLine + 1) return cardLefts[i] - snapPadding;
      }
      return null;
    }

    for (i = cardLefts.length - 1; i >= 0; i--) {
      if (cardLefts[i] < snapLine - 1) return Math.max(0, cardLefts[i] - snapPadding);
    }
    return null;
  }

  // Where the auto slide goes next: the next card (the browser clamps the last step to the end), or back to the
  // start once the track is at its end
  function autoplayScrollLeft(cardLefts, scrollLeft, snapPadding, maxScroll) {
    if (scrollLeft >= maxScroll - 1) return 0;
    var left = targetScrollLeft(cardLefts, scrollLeft, snapPadding, 1);
    return left === null ? 0 : Math.min(left, maxScroll);
  }

  // Every scrollLeft the track can rest at, one per dot, clamped to the end
  function stopPositions(cardLefts, snapPadding, maxScroll) {
    var positions = [];
    cardLefts.forEach(function (left) {
      var position = Math.min(Math.max(0, left - snapPadding), Math.max(0, maxScroll));
      if (!positions.length || position - positions[positions.length - 1] > 1) positions.push(position);
    });
    return positions;
  }

  function nearestStop(positions, scrollLeft) {
    var nearest = 0;
    for (var i = 1; i < positions.length; i++) {
      if (Math.abs(positions[i] - scrollLeft) < Math.abs(positions[nearest] - scrollLeft)) nearest = i;
    }
    return nearest;
  }

  function snapPaddingOf(track) {
    return parseFloat(window.getComputedStyle(track).scrollPaddingLeft) || 0;
  }

  // The buttons sit level with the middle of the first photo in the slider (Impulse centres its Flickity buttons
  // on the slide; here the slide also holds text, so the photo is the reference)
  function alignArrows(slider, buttons) {
    var media = slider.querySelector('.fm-disney-features__media, .grid-product__image-wrap, img');
    if (!media) return;
    var top = media.getBoundingClientRect().top - slider.getBoundingClientRect().top + media.offsetHeight / 2;
    buttons.forEach(function (button) {
      button.style.top = Math.round(top) + 'px';
    });
  }

  /* ---------- Feature slider ---------- */

  /*
   * - Prev and next (Impulse's slider buttons) bring the next or previous slide to the start of the track; the
   *   one at either end is disabled, which the theme's .flickity-button:disabled hides.
   * - The dots mark the slide in view and jump on a click; they are rebuilt when the number of stops differs.
   * - Auto slide, when the section carries data-autoplay (seconds between slides): the next slide, back to the
   *   first after the last. A click, a press, a swipe, a sideways scroll, a key or focus moving into the slider
   *   stops it, and it slides again once data-resume seconds pass without another. It waits while less than half
   *   of the slider is on screen, the tab is hidden or the theme editor holds a block, and never runs for visitors
   *   who ask for reduced motion.
   * - Every photo loads once the slider reaches the screen, so no slide brings in an empty frame.
   */
  function Features(section) {
    if (section.hasAttribute('data-fm-ready')) return;

    var track = section.querySelector('[data-fm-features-track]');
    var slider = section.querySelector('[data-fm-slider]');
    var prev = section.querySelector('[data-fm-prev]');
    var next = section.querySelector('[data-fm-next]');
    var dots = section.querySelector('[data-fm-dots]');
    if (!track || !slider || !prev || !next) return;

    section.setAttribute('data-fm-ready', '');

    var frame = 0;
    var cleanups = [];
    var stops = [];

    function listen(target, type, handler, options) {
      target.addEventListener(type, handler, options);
      cleanups.push(function () {
        target.removeEventListener(type, handler, options);
      });
    }

    function cardLefts() {
      var origin = track.firstElementChild ? track.firstElementChild.offsetLeft : 0;
      return Array.prototype.map.call(track.children, function (card) {
        return card.offsetLeft - origin;
      });
    }

    function maxScroll() {
      return track.scrollWidth - track.clientWidth;
    }

    function updateDots(max) {
      if (!dots) return;

      var positions = stopPositions(cardLefts(), snapPaddingOf(track), max);

      if (positions.length !== dots.children.length) {
        dots.textContent = '';
        positions.forEach(function (position, index) {
          var dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'fm-disney-features__dot';
          dot.setAttribute('aria-controls', track.id);
          dot.setAttribute('aria-label', 'Slide ' + (index + 1) + ' of ' + positions.length);
          dots.appendChild(dot);
        });
      }

      stops = positions;
      dots.hidden = stops.length < 2;

      var current = nearestStop(stops, track.scrollLeft);
      Array.prototype.forEach.call(dots.children, function (dot, index) {
        var active = index === current;
        dot.classList.toggle('is-active', active);
        if (active) {
          dot.setAttribute('aria-current', 'true');
        } else {
          dot.removeAttribute('aria-current');
        }
      });
    }

    function update() {
      frame = 0;
      var max = maxScroll();
      var scrollable = max > 1;
      section.setAttribute('data-fm-scrollable', scrollable ? 'true' : 'false');
      prev.disabled = !scrollable || track.scrollLeft <= 1;
      next.disabled = !scrollable || track.scrollLeft >= max - 1;
      alignArrows(slider, [prev, next]);
      updateDots(max);
    }

    function scheduleUpdate() {
      if (!frame) frame = window.requestAnimationFrame(update);
    }

    function step(direction) {
      var left = targetScrollLeft(cardLefts(), track.scrollLeft, snapPaddingOf(track), direction);
      if (left !== null) track.scrollTo({ left: left, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
    }

    listen(prev, 'click', function () { step(-1); });
    listen(next, 'click', function () { step(1); });

    if (dots) {
      listen(dots, 'click', function (event) {
        var dot = event.target.closest('.fm-disney-features__dot');
        var index = dot ? Array.prototype.indexOf.call(dots.children, dot) : -1;
        if (index >= 0 && stops[index] !== undefined) {
          track.scrollTo({ left: stops[index], behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
        }
      });
    }

    listen(track, 'scroll', scheduleUpdate, { passive: true });

    var resizeObserver = null;
    if (window.ResizeObserver) {
      resizeObserver = new ResizeObserver(scheduleUpdate);
      resizeObserver.observe(track);
    } else {
      listen(window, 'resize', scheduleUpdate);
    }

    // Auto slide

    var interval = parseFloat(section.getAttribute('data-autoplay')) * 1000 || 0;
    var resumeDelay = parseFloat(section.getAttribute('data-resume')) * 1000 || 0;
    var slideTimer = 0;
    var resumeTimer = 0;
    var stoppedByVisitor = false;
    var heldByEditor = false;
    var onScreen = false;
    var touching = false;
    var intersectionObserver = null;

    function canSlide() {
      return interval > 0 && !stoppedByVisitor && !heldByEditor && onScreen && !document.hidden &&
        !prefersReducedMotion() && maxScroll() > 1;
    }

    // A full interval from now before the next slide, or none when sliding is not allowed now
    function restartCountdown() {
      window.clearTimeout(slideTimer);
      slideTimer = canSlide() ? window.setTimeout(slide, interval) : 0;
    }

    function slide() {
      slideTimer = 0;
      if (!canSlide()) return;
      track.scrollTo({ left: autoplayScrollLeft(cardLefts(), track.scrollLeft, snapPaddingOf(track), maxScroll()), behavior: 'smooth' });
      restartCountdown();
    }

    // The visitor used the slider: stop, and slide again once resumeDelay passes with no further use
    function stopForVisitor() {
      stoppedByVisitor = true;
      window.clearTimeout(slideTimer);
      window.clearTimeout(resumeTimer);
      slideTimer = 0;
      resumeTimer = window.setTimeout(function () {
        resumeTimer = 0;
        stoppedByVisitor = false;
        slide();
      }, resumeDelay);
    }

    if (interval > 0) {
      listen(section, 'click', stopForVisitor);
      listen(section, 'keydown', stopForVisitor);
      listen(section, 'focusin', stopForVisitor);
      listen(section, 'pointerdown', function (event) {
        if (event.pointerType !== 'touch') stopForVisitor();
      });

      listen(track, 'wheel', function (event) {
        if (Math.abs(event.deltaX) > Math.abs(event.deltaY) || (event.shiftKey && event.deltaY)) stopForVisitor();
      }, { passive: true });

      // A swipe: the track scrolls while a finger is on it. A finger that only scrolls the page moves no track.
      listen(track, 'touchstart', function () { touching = true; }, { passive: true });
      listen(track, 'touchend', function () { touching = false; }, { passive: true });
      listen(track, 'touchcancel', function () { touching = false; }, { passive: true });
      listen(track, 'scroll', function () {
        if (touching) stopForVisitor();
      }, { passive: true });

      listen(document, 'visibilitychange', restartCountdown);

      if (reduceMotionQuery && reduceMotionQuery.addEventListener) {
        listen(reduceMotionQuery, 'change', restartCountdown);
      }

      listen(section, 'shopify:block:select', function () {
        heldByEditor = true;
        restartCountdown();
      });
      listen(section, 'shopify:block:deselect', function () {
        heldByEditor = false;
        restartCountdown();
      });
    }

    // The browser's lazy loading starts a photo only as it slides into view, so an arrow or the auto slide would
    // bring in an empty frame
    function loadAllPhotos() {
      Array.prototype.forEach.call(track.querySelectorAll('img[loading="lazy"]'), function (image) {
        image.loading = 'eager';
      });
    }

    if (window.IntersectionObserver) {
      intersectionObserver = new IntersectionObserver(function (entries) {
        var entry = entries[entries.length - 1];
        var wasOnScreen = onScreen;
        if (entry.isIntersecting) loadAllPhotos();
        onScreen = entry.intersectionRatio >= 0.5;
        if (onScreen !== wasOnScreen) restartCountdown();
      }, { threshold: [0, 0.5, 1] });
      intersectionObserver.observe(track);
    } else {
      loadAllPhotos();
      onScreen = true;
      restartCountdown();
    }

    section.fmDestroy = function () {
      window.clearTimeout(slideTimer);
      window.clearTimeout(resumeTimer);
      window.cancelAnimationFrame(frame);
      if (resizeObserver) resizeObserver.disconnect();
      if (intersectionObserver) intersectionObserver.disconnect();
      cleanups.forEach(function (cleanup) { cleanup(); });
    };

    update();
  }

  /* ---------- Related products ---------- */

  // Ported from OzMobiles' assets/ranked-related-products.js. Prev and next page by whole cards: next brings the
  // first card cut off on the right to the start, prev goes back by as many cards as fit. The start is where the
  // first card sits unscrolled (the theme's phone gutter). The button at either end is disabled, which the theme's
  // .flickity-button:disabled hides, so both are hidden when every card fits.
  function setupRelatedArrows(root) {
    var slider = root.querySelector('[data-fm-slider]');
    var scroller = root.querySelector('[data-fm-related-scroller]');
    var grid = root.querySelector('[data-fm-related-grid]');
    var prev = root.querySelector('[data-fm-prev]');
    var next = root.querySelector('[data-fm-next]');
    var frame = 0;
    if (!slider || !scroller || !grid || !prev || !next) return;

    function update() {
      frame = 0;
      var max = scroller.scrollWidth - scroller.clientWidth;
      var scrollable = max > 1;
      root.setAttribute('data-fm-scrollable', scrollable ? 'true' : 'false');
      prev.disabled = !scrollable || scroller.scrollLeft <= 1;
      next.disabled = !scrollable || scroller.scrollLeft >= max - 1;
      alignArrows(slider, [prev, next]);
    }

    function scheduleUpdate() {
      if (!frame) frame = window.requestAnimationFrame(update);
    }

    function step(direction, button) {
      if (button.disabled) return;

      var origin = scroller.getBoundingClientRect().left - scroller.scrollLeft;
      var cards = Array.prototype.map.call(grid.children, function (card) {
        var rect = card.getBoundingClientRect();
        return { left: rect.left - origin, right: rect.right - origin };
      });
      if (!cards.length) return;

      var inset = cards[0].left;
      var target = null;
      var i;

      if (direction > 0) {
        var viewEnd = scroller.scrollLeft + scroller.clientWidth;
        for (i = 0; i < cards.length; i++) {
          if (cards[i].right > viewEnd + 1) {
            target = cards[i].left - inset;
            break;
          }
        }
        if (target === null) target = scroller.scrollWidth;
      } else {
        var wanted = scroller.scrollLeft - (scroller.clientWidth - inset);
        for (i = 0; i < cards.length; i++) {
          if (cards[i].left - inset >= wanted - 1) {
            target = cards[i].left - inset;
            break;
          }
        }
      }

      scroller.scrollTo({ left: Math.max(0, target || 0), behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
    }

    prev.addEventListener('click', function () { step(-1, prev); });
    next.addEventListener('click', function () { step(1, next); });
    scroller.addEventListener('scroll', scheduleUpdate, { passive: true });

    if (window.ResizeObserver) {
      new ResizeObserver(scheduleUpdate).observe(scroller);
    } else {
      window.addEventListener('resize', scheduleUpdate);
    }

    // Photos arrive after the cards, and the buttons follow the photo's middle
    Array.prototype.forEach.call(grid.querySelectorAll('img'), function (image) {
      if (!image.complete) image.addEventListener('load', scheduleUpdate, { once: true });
    });

    update();
  }

  function fetchCandidates(url) {
    return fetch(url, { credentials: 'same-origin' })
      .then(function (response) { return response.ok ? response.text() : ''; })
      .then(function (text) {
        var html = new DOMParser().parseFromString(text, 'text/html');
        return Array.prototype.slice.call(html.querySelectorAll('[data-fm-related-results] [data-handle]'));
      })
      .catch(function () { return []; });
  }

  // The rules (data-fm-related-rules) run together, then the cards are taken rule by rule, closest price first
  // within each rule, until the section's limit. A product shows once and this product never.
  function Related(el) {
    if (el.hasAttribute('data-fm-ready')) return;
    el.setAttribute('data-fm-ready', '');

    var price = parseInt(el.dataset.price, 10) || 0;
    var limit = parseInt(el.dataset.limit, 10) || 16;
    var grid = el.querySelector('[data-fm-related-grid]');
    var rules = [];

    try {
      rules = JSON.parse(el.querySelector('[data-fm-related-rules]').textContent);
    } catch (error) {
      return;
    }
    if (!grid || !rules.length) return;

    Promise.all(rules.map(function (rule) { return fetchCandidates(rule.url); }))
      .then(function (results) {
        var shown = {};
        var added = 0;
        shown[el.dataset.handle] = true;

        rules.forEach(function (rule, ruleIndex) {
          var fromRule = 0;

          results[ruleIndex]
            .map(function (item, order) {
              var itemPrice = parseInt(item.dataset.price, 10) || 0;
              return { item: item, price: itemPrice, distance: Math.abs(itemPrice - price), order: order };
            })
            .sort(function (a, b) {
              return (a.distance - b.distance) || (a.price - b.price) || (a.order - b.order);
            })
            .forEach(function (entry) {
              var handle = entry.item.dataset.handle;
              var card = entry.item.querySelector('.grid__item');
              if (added >= limit || fromRule >= rule.limit || shown[handle] || !card) return;
              shown[handle] = true;
              grid.appendChild(document.importNode(card, true));
              fromRule++;
              added++;
            });
        });

        el.setAttribute('data-fm-count', String(added));
        if (!added) return;

        el.classList.remove('hide');
        if (window.theme && theme.reinitProductGridItem) theme.reinitProductGridItem(el);
        if (window.AOS) AOS.refreshHard();
        setupRelatedArrows(el);
      });
  }

  /* ---------- Start ---------- */

  /* Exposed so a section added later (theme editor, Section Rendering) can be started. */
  window.FmDisney = { Main: Main, Features: Features, Related: Related };

  function start() {
    document.querySelectorAll('[data-fm-disney]').forEach(function (el) { new Main(el); });
    document.querySelectorAll('[data-fm-features]').forEach(function (el) { Features(el); });
    document.querySelectorAll('[data-fm-related]').forEach(function (el) { Related(el); });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  // Theme editor: a re-rendered section starts again; a removed slider stops its timers
  document.addEventListener('shopify:section:load', function (event) {
    event.target.querySelectorAll('[data-fm-features]').forEach(function (el) { Features(el); });
    event.target.querySelectorAll('[data-fm-related]').forEach(function (el) { Related(el); });
  });

  document.addEventListener('shopify:section:unload', function (event) {
    event.target.querySelectorAll('[data-fm-features]').forEach(function (el) {
      if (el.fmDestroy) el.fmDestroy();
    });
  });
})();
